import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || ''
);

export interface LocationAnalysis {
  demographics: {
    age: string;
    income: string;
    education: string;
  };
  market: {
    size: string;
    growth: string;
    competition: string;
  };
  property: {
    recommendedSize: string;
    estimatedRent: string;
    type: string;
  };
  score: {
    overall: number;
    location: number;
    market: number;
    competition: number;
  };
  recommendations: string[];
  risks: string[];
}

interface CachedSearch {
  key: string;
  data: LocationAnalysis;
  timestamp: number;
}

const CACHE_DURATION = 1000 * 60 * 30; // 30 minutes

export function getCacheKey(location: string, businessType: string, budget: string): string {
  return `${location}|${businessType}|${budget}`.toLowerCase();
}

export function getSearchFromCache(location: string, businessType: string, budget: string): LocationAnalysis | null {
  try {
    const cacheKey = getCacheKey(location, businessType, budget);
    const cached = localStorage.getItem(`search_${cacheKey}`);
    
    if (cached) {
      const parsedCache: CachedSearch = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - parsedCache.timestamp < CACHE_DURATION) {
        return parsedCache.data;
      } else {
        localStorage.removeItem(`search_${cacheKey}`);
      }
    }
  } catch (error) {
    console.error('Error reading from cache:', error);
  }
  return null;
}

export function saveSearchToCache(location: string, businessType: string, budget: string, data: LocationAnalysis): void {
  try {
    const cacheKey = getCacheKey(location, businessType, budget);
    const cacheData: CachedSearch = {
      key: cacheKey,
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(`search_${cacheKey}`, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error saving to cache:', error);
  }
}

export async function analyzeLocation(location: string, businessType: string, budget: string): Promise<LocationAnalysis> {
  // Check cache first
  const cachedResult = getSearchFromCache(location, businessType, budget);
  if (cachedResult) {
    return cachedResult;
  }

  // If not in cache, make API call
  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-location`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      location,
      businessType,
      budget,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to analyze location');
  }

  const data = await response.json();
  
  // Save to cache
  saveSearchToCache(location, businessType, budget, data);
  
  return data;
}